import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Type } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, map, shareReplay, tap } from 'rxjs';

import { MAIN_CONTENT_ROUTE_DATA_KEY } from '@ppg/core/content';
import { MixedContentPageType } from '@ppg/core/enums';
import { Content } from '@ppg/core/models';
import { createPendingTaskWatcher } from '@ppg/core/services';
import { BreadcrumbsComponent } from '@ppg/shared/breadcrumbs';

import { PageResolverComponent } from '../cms-content/page-resolver.component';
import { MixedContentPageResolverComponent } from '../mixed-content/mixed-content-page-resolver.component';

@Component({
  selector: 'ppg-preview-content-page-resolver',
  standalone: true,
  imports: [CommonModule, BreadcrumbsComponent],
  templateUrl: './preview-content-page-resolver.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewContentPageResolverComponent {
  readonly #activatedRoute = inject(ActivatedRoute);

  readonly #routeData = toSignal(this.#activatedRoute.data, { initialValue: this.#activatedRoute.snapshot.data });
  readonly #mainContent = computed<Content | null>(() => this.#routeData()[MAIN_CONTENT_ROUTE_DATA_KEY] ?? null);

  readonly isMixedContent = computed(() => {
    const pageType = this.#mainContent()?.page?.type;
    return pageType
      ? { value: Object.values(MixedContentPageType).includes(pageType as MixedContentPageType) }
      : undefined;
  });

  readonly #pageRendererComponentLoadTask = createPendingTaskWatcher();
  readonly pageRendererComponent$ = toObservable(this.isMixedContent).pipe(
    filter(Boolean),
    tap(() => this.#pageRendererComponentLoadTask.startTask()),
    map((isMixedContent) => this.#getPageRenderer(isMixedContent.value)),
    tap(() => this.#pageRendererComponentLoadTask.stopTask()),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  #getPageRenderer(isMixedContent: boolean): Type<unknown> {
    return isMixedContent ? MixedContentPageResolverComponent : PageResolverComponent;
  }
}
